import React, { useMemo, useState, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  BackgroundVariant
} from 'reactflow';
import { Box, Paper, Typography, Chip, Alert, } from '@mui/material';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import 'reactflow/dist/style.css';
import Popover from "@mui/material/Popover";

// Import dynamic node component
import WorkflowNode from '../WorkflowBuilder/components/WorkflowNode';

import NodeExecutionDetails from '../components/NodeExecutionDetails';

const nodeTypes = {
  // All nodes now use the WorkflowNode component
  dynamicNode: WorkflowNode,
};

const ExecutionFlowDiagram = ({ nodes = [], edges = [], nodeLogs = {}, currentRunningNode = null }) => {
  
  // Enhance nodes with execution status and error indicators
  const flowNodes = useMemo(() => {
    return nodes.map(node => {
      const nodeLog = nodeLogs[node.id] || {};
      const isRunning = currentRunningNode === node.id;
      
      // Extract error information
      const hasError = nodeLog.status === 'failed' || nodeLog.error;
      const errorCategory = nodeLog.error?.category || 'unknown';
      const errorSeverity = nodeLog.error?.severity || 'medium';
      
      // Determine error badge color
      let errorBadgeColor = '#6c757d';
      switch (errorSeverity) {
        case 'critical': errorBadgeColor = '#dc3545'; break;
        case 'high': errorBadgeColor = '#fd7e14'; break;
        case 'medium': errorBadgeColor = '#ffc107'; break;
        case 'low': errorBadgeColor = '#28a745'; break;
      }

      return {
        ...node,
        type: 'dynamicNode', // All nodes now use the WorkflowNode component
        data: {
          ...node.data,
          executionStatus: isRunning ? 'running' : nodeLog.status || 'pending',
          executionTime: nodeLog.executionTime,
          error: nodeLog.error,
          output: nodeLog.output,
          // Enhanced error information
          hasError,
          errorCategory: errorCategory.replace('_', ' '),
          errorSeverity,
          errorBadgeColor,
          tokensUsed: nodeLog.tokensUsed || nodeLog.output?.usage?.totalTokens || 0,
          cost: nodeLog.cost || nodeLog.output?.cost || 0,
          // Add visual indicators
          style: {
            ...node.data?.style,
            opacity: nodeLog.status === 'skipped' ? 0.5 : 1,
            borderColor: hasError ? errorBadgeColor : node.data?.style?.borderColor,
            borderWidth: hasError ? '2px' : node.data?.style?.borderWidth || '1px',
          }
        }
      };
    });
  }, [nodes, nodeLogs, currentRunningNode]);

  // Color edges based on execution flow
  const flowEdges = useMemo(() => {
    return edges.map(edge => {
      const sourceLog = nodeLogs[edge.source];
      const targetLog = nodeLogs[edge.target];

      let edgeColor = '#b1b1b7'; // Default gray
      let animated = false;

      if (sourceLog?.status === 'completed' && targetLog?.status) {
        edgeColor = '#4caf50'; // Green for executed path
      } else if (currentRunningNode === edge.source || currentRunningNode === edge.target) {
        edgeColor = '#2196f3'; // Blue for active
        animated = true;
      } else if (sourceLog?.status === 'failed') {
        edgeColor = '#f44336'; // Red for failed path
      }

      return {
        ...edge,
        style: {
          ...edge.style,
          stroke: edgeColor,
          strokeWidth: 2,
        },
        animated,
      };
    });
  }, [edges, nodeLogs, currentRunningNode]);

  const getExecutionStats = () => {
    const stats = {
      total: nodes.length,
      completed: 0,
      running: 0,
      failed: 0,
      skipped: 0,
      pending: 0,
      errors: {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0
      },
      totalTokens: 0,
      totalCost: 0,
    };

    Object.values(nodeLogs).forEach(log => {
      if (log.status === 'completed') stats.completed++;
      else if (log.status === 'running') stats.running++;
      else if (log.status === 'failed') {
        stats.failed++;
        // Count error severity
        const severity = log.error?.severity || 'medium';
        if (stats.errors[severity] !== undefined) {
          stats.errors[severity]++;
        }
      }
      else if (log.status === 'skipped') stats.skipped++;
      
      // Aggregate metrics
      if (log.tokensUsed) stats.totalTokens += log.tokensUsed;
      if (log.output?.usage?.totalTokens) stats.totalTokens += log.output.usage.totalTokens;
      if (log.cost) stats.totalCost += log.cost;
      if (log.output?.cost) stats.totalCost += log.output.cost;
    });

    stats.pending = stats.total - (stats.completed + stats.running + stats.failed + stats.skipped);

    return stats;
  };

  const stats = getExecutionStats();


  const [open, setOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  const handleNodeClick = (_, node) => {
    setSelectedNode(node.id);
    setOpen(true); // open modal
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedNode(null);
  };

  return (
    <div className='executions-fllow-sec  '>
      <div className='title-area'>
        <div className='left-area'>
          <h4>Execution Flow</h4>
        </div>
        <div className='right-area' style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
          <Chip label={`Total: ${stats.total}`} size="small" />
          {stats.completed > 0 && (<Chip label={`Completed: ${stats.completed}`} color="success" size="small" />)}
          {stats.running > 0 && (<Chip label={`Running: ${stats.running}`} color="primary" size="small" />)}
          {stats.failed > 0 && (<Chip label={`Failed: ${stats.failed}`} color="error" size="small" />)}
          {stats.skipped > 0 && (<Chip label={`Skipped: ${stats.skipped}`} size="small" sx={{ opacity: 0.6 }} />)}
          {stats.pending > 0 && (<Chip label={`Pending: ${stats.pending}`} variant="outlined" size="small" />)}
          
          {/* Error severity breakdown */}
          {stats.errors.critical > 0 && (
            <Chip 
              label={`Critical: ${stats.errors.critical}`} 
              size="small" 
              sx={{ backgroundColor: '#dc3545', color: 'white' }} 
            />
          )}
          {stats.errors.high > 0 && (
            <Chip 
              label={`High: ${stats.errors.high}`} 
              size="small" 
              sx={{ backgroundColor: '#fd7e14', color: 'white' }} 
            />
          )}
          {stats.errors.medium > 0 && (
            <Chip 
              label={`Medium: ${stats.errors.medium}`} 
              size="small" 
              sx={{ backgroundColor: '#ffc107', color: 'black' }} 
            />
          )}
          {stats.errors.low > 0 && (
            <Chip 
              label={`Low: ${stats.errors.low}`} 
              size="small" 
              sx={{ backgroundColor: '#28a745', color: 'white' }} 
            />
          )}
          
          {/* Performance metrics */}
          {stats.totalTokens > 0 && (
            <Chip 
              label={`Tokens: ${stats.totalTokens}`} 
              size="small" 
              variant="outlined"
              sx={{ borderColor: '#17a2b8', color: '#17a2b8' }}
            />
          )}
          {stats.totalCost > 0 && (
            <Chip 
              label={`Cost: $${stats.totalCost.toFixed(4)}`} 
              size="small" 
              variant="outlined"
              sx={{ borderColor: '#6f42c1', color: '#6f42c1' }}
            />
          )}
        </div>
      </div>
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ height: '650px', overflow: 'hidden' }}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              nodeTypes={nodeTypes}
              fitView
              onNodeClick={handleNodeClick}
              fitViewOptions={{ padding: 0.2 }}
              attributionPosition="bottom-left"
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              panOnDrag={false}
              preventScrolling={false}
            >
              <Controls showInteractive={false} />
              <MiniMap
                nodeColor={node => {
                  const log = nodeLogs[node.id];
                  if (currentRunningNode === node.id) return '#2196f3';
                  if (log?.status === 'completed') return '#4caf50';
                  if (log?.status === 'failed') return '#f44336';
                  if (log?.status === 'skipped') return '#9e9e9e';
                  return '#e0e0e0';
                }}
              />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

            </ReactFlow>
          </ReactFlowProvider>
        </Paper>
      </Box>


      {/* ✅ Modal for clicked node */}
      <Modal isOpen={open} onClose={handleClose} className={`main-modal new-user-modal modal-dialog-centered workflow-modal modal-lg`}>
        <ModalHeader toggle={handleClose}>
          <h3>Node Details</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='form-area'>
              {selectedNode && nodeLogs[selectedNode] ? (
                <Box>
                  <NodeExecutionDetails
                    log={nodeLogs[selectedNode]}
                    nodeId={selectedNode}
                  />
                </Box>
              ) : (
                <Alert severity="info">
                  No details available for this node.
                </Alert>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>

  );
};

export default ExecutionFlowDiagram;